import Decimal from 'decimal.js-light';

Decimal.set({
  precision: 50,
  rounding: Decimal.ROUND_HALF_UP,
});

function getDecimalScale(decimals) {
  const scale = Number.parseInt(decimals, 10);

  return Number.isFinite(scale) && scale > 0 ? scale : 0;
}

export function parseDecimalInput(value) {
  const normalized = String(value ?? '').trim().replace(',', '.');

  if (
    normalized === ''
    || normalized === '-'
    || normalized === '.'
    || normalized === '-.'
  ) {
    return null;
  }

  try {
    return new Decimal(normalized);
  } catch {
    return null;
  }
}

export function getFractionLength(value) {
  const normalized = String(value ?? '').trim().replace(',', '.');
  const fraction = normalized.split('.')[1];

  return fraction ? fraction.length : 0;
}

export function sanitizeDecimalInput(value, decimals, options = {}) {
  const { allowNegative = false } = options;
  const decimalScale = getDecimalScale(decimals);
  const rawValue = String(value ?? '').replace(',', '.');
  const isNegative = allowNegative && rawValue.trim().startsWith('-');
  const normalized = rawValue.replace(/[^\d.]/g, '');
  const [integerPart = '', ...decimalParts] = normalized.split('.');
  let sanitizedValue = '';

  if (decimalScale <= 0) {
    sanitizedValue = integerPart;
    return isNegative ? (sanitizedValue ? `-${sanitizedValue}` : '-') : sanitizedValue;
  }

  const fractionPart = decimalParts.join('').slice(0, decimalScale);

  if (!decimalParts.length) {
    sanitizedValue = integerPart;
    return isNegative ? (sanitizedValue ? `-${sanitizedValue}` : '-') : sanitizedValue;
  }

  if (normalized.startsWith('.')) {
    sanitizedValue = fractionPart ? `0.${fractionPart}` : '0.';
    return isNegative ? `-${sanitizedValue}` : sanitizedValue;
  }

  sanitizedValue = `${integerPart}.${fractionPart}`;
  return isNegative ? `-${sanitizedValue}` : sanitizedValue;
}

export function normalToScaledInt(value, decimals, rounding = Decimal.ROUND_HALF_UP) {
  const decimal = parseDecimalInput(value);

  if (decimal == null) {
    return null;
  }

  return decimal
    .mul(new Decimal(10).pow(getDecimalScale(decimals)))
    .toFixed(0, rounding);
}

export function scaledIntToDecimal(value, decimals) {
  const integerValue = new Decimal(String(value || 0).trim().replace(',', '.'))
    .toDecimalPlaces(0, Decimal.ROUND_DOWN);

  return integerValue.div(new Decimal(10).pow(getDecimalScale(decimals)));
}

export function scaledIntToNormal(value, decimals, options = {}) {
  const { fixed = true, trimZeros = false } = options;
  const decimal = scaledIntToDecimal(value, decimals);
  let output = fixed ? decimal.toFixed(getDecimalScale(decimals)) : decimal.toString();

  if (trimZeros && output.includes('.')) {
    output = output.replace(/\.?0+$/, '');
  }

  return output;
}

export function addThousandsSeparators(integerPart) {
  return integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function formatDecimal(value, decimals, options = {}) {
  const {
    trimZeros = true,
    minFractionDigits = 0,
    rounding = Decimal.ROUND_HALF_UP,
    signDisplay = 'auto',
    prefix = '',
    suffix = '',
  } = options;
  const decimal = value instanceof Decimal ? value : parseDecimalInput(String(value ?? ''));

  if (decimal == null) {
    return '';
  }

  const isNegative = decimal.lt(0);
  const absolute = decimal.abs();
  let normal = absolute.toFixed(getDecimalScale(decimals), rounding);

  if (trimZeros && normal.includes('.')) {
    normal = normal.replace(/\.?0+$/, '');
  }

  if (minFractionDigits > 0) {
    const [integerPart, fractionPart = ''] = normal.split('.');
    normal = `${integerPart}.${fractionPart.padEnd(minFractionDigits, '0')}`;
  }

  const [integerPart, fractionPart] = normal.split('.');
  const grouped = addThousandsSeparators(integerPart);
  const sign = isNegative ? '-' : signDisplay === 'always' ? '+' : '';
  const fraction = fractionPart ? `.${fractionPart}` : '';

  return `${sign}${prefix}${grouped}${fraction}${suffix}`;
}

export function formatScaledInt(value, decimals, options = {}) {
  return formatDecimal(scaledIntToDecimal(value, decimals), decimals, options);
}

export function decimalToString(value, fallback = '0') {
  const decimal = value instanceof Decimal ? value : parseDecimalInput(value);

  return decimal == null ? fallback : decimal.toString();
}

export function decimalGt(left, right) {
  const leftDecimal = parseDecimalInput(left);
  const rightDecimal = parseDecimalInput(right);

  return Boolean(leftDecimal && rightDecimal && leftDecimal.gt(rightDecimal));
}

export function decimalGte(left, right) {
  const leftDecimal = parseDecimalInput(left);
  const rightDecimal = parseDecimalInput(right);

  return Boolean(leftDecimal && rightDecimal && leftDecimal.gte(rightDecimal));
}

export function decimalEq(left, right) {
  const leftDecimal = parseDecimalInput(left);
  const rightDecimal = parseDecimalInput(right);

  return Boolean(leftDecimal && rightDecimal && leftDecimal.eq(rightDecimal));
}

export function addDecimalStrings(...values) {
  return values
    .reduce((total, value) => total.plus(parseDecimalInput(value) || 0), new Decimal(0))
    .toString();
}

export function subtractDecimalStrings(left, right) {
  const leftDecimal = parseDecimalInput(left) || new Decimal(0);
  const rightDecimal = parseDecimalInput(right) || new Decimal(0);
  const result = leftDecimal.minus(rightDecimal);

  return result.lt(0) ? '0' : result.toString();
}

export function multiplyDecimalStrings(left, right) {
  const leftDecimal = parseDecimalInput(left) || new Decimal(0);
  const rightDecimal = parseDecimalInput(right) || new Decimal(0);

  return leftDecimal.mul(rightDecimal).toString();
}

export function divideDecimalStrings(left, right) {
  const leftDecimal = parseDecimalInput(left) || new Decimal(0);
  const rightDecimal = parseDecimalInput(right);

  if (!rightDecimal || rightDecimal.eq(0)) {
    return '0';
  }

  return leftDecimal.div(rightDecimal).toString();
}

export function decimalPercentString(value, total, max = '100') {
  const valueDecimal = parseDecimalInput(value) || new Decimal(0);
  const totalDecimal = parseDecimalInput(total);

  if (!totalDecimal || totalDecimal.lte(0)) {
    return '0';
  }

  const percent = valueDecimal.div(totalDecimal).mul(100);
  const maxDecimal = parseDecimalInput(max) || new Decimal(100);
  const cappedPercent = percent.gt(maxDecimal) ? maxDecimal : percent;

  return cappedPercent.toFixed(2).replace(/\.?0+$/, '');
}

export function isPositiveDecimalInput(value) {
  const decimal = parseDecimalInput(value);

  return Boolean(decimal && decimal.gt(0));
}

export { Decimal };
